import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const STATUSES = ['Present', 'Absent', 'Late', 'Half-Day', 'Leave'];

// Category definitions used by the clickable cards
const CATEGORIES = [
  { value: 'all',                  label: 'All Categories',      icon: 'fa-users',            color: 'text-gray-700',   border: 'border-gray-500',   bg: 'bg-gray-50' },
  { value: 'Teaching Faculty',     label: 'Teaching Faculty',    icon: 'fa-chalkboard-teacher', color: 'text-green-700',  border: 'border-green-500',  bg: 'bg-green-50' },
  { value: 'Administrative Staff', label: 'Administrative Staff',icon: 'fa-user-tie',         color: 'text-indigo-700', border: 'border-indigo-500', bg: 'bg-indigo-50' },
  { value: 'Support Staff',        label: 'Support Staff (Class IV)', icon: 'fa-broom',       color: 'text-gray-700',   border: 'border-gray-500',   bg: 'bg-gray-50' },
];

const todayStr = () => new Date().toISOString().split('T')[0];
const offsetDate = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const categoryBadge = (cat) => {
  if (cat === 'Teaching Faculty')     return 'bg-green-100 text-green-800';
  if (cat === 'Administrative Staff') return 'bg-indigo-100 text-indigo-800';
  if (cat === 'Support Staff')        return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-700';
};

export default function StaffAttendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // ─── Date range ───────────────────────────────────────
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo]     = useState(todayStr());
  const [activePreset, setActivePreset] = useState('today');

  // ─── Filters (only what matters) ──────────────────────
  const [categoryFilter, setCategoryFilter] = useState('all');   // card-driven
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // ─── Data ─────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // ─── Admin edit modal ─────────────────────────────────
  const [editTimes, setEditTimes] = useState(null);
  const [editForm, setEditForm] = useState({ check_in: '', check_out: '' });
  const [savingTimes, setSavingTimes] = useState(false);

  useEffect(() => { loadAll(); }, [dateFrom, dateTo, statusFilter, categoryFilter]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo)   params.append('date_to', dateTo);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const [a, p] = await Promise.all([
        api.get(`/staff-attendance/?${params.toString()}`),
        api.get('/staff-attendance/people'),
      ]);
      setRows(a.data || []);
      setAllPeople(p.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ─── Date presets ─────────────────────────────────────
  const applyPreset = (preset) => {
    setActivePreset(preset);
    const today = todayStr();
    switch (preset) {
      case 'today':     setDateFrom(today);           setDateTo(today); break;
      case 'yesterday': setDateFrom(offsetDate(-1));  setDateTo(offsetDate(-1)); break;
      case 'week':      setDateFrom(offsetDate(-6));  setDateTo(today); break;
      case '30days':    setDateFrom(offsetDate(-29)); setDateTo(today); break;
      case 'month': {
        const d = new Date();
        const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        setDateFrom(first); setDateTo(today); break;
      }
      case 'all':       setDateFrom('');              setDateTo(''); break;
      default: break;
    }
  };

  // ─── Actions ──────────────────────────────────────────
  const handleBulkInit = async () => {
    const target = dateFrom || todayStr();
    if (!window.confirm(`Pre-mark all people as Absent for ${target}?`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/staff-attendance/bulk-mark?day=${target}`);
      alert(`Created ${res.data.created} records (${res.data.total_people} people total).`);
      loadAll();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const handleCheckIn = async (row) => {
    setBusy(true);
    try {
      await api.post('/staff-attendance/check-in', {
        person_type: row.person_type, person_id: row.person_id,
        date: row.date, check_in: new Date().toISOString(), status: 'Present',
      });
      loadAll();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const handleCheckOut = async (row) => {
    setBusy(true);
    try {
      await api.post(`/staff-attendance/check-out/${row.id}`);
      loadAll();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const handleStatusChange = async (row, status) => {
    try {
      await api.put(`/staff-attendance/${row.id}`, { status });
      loadAll();
    } catch { alert('Failed'); }
  };

  const handleManualAdd = async (ptype, person) => {
    const target = dateFrom || todayStr();
    try {
      await api.post('/staff-attendance/check-in', {
        person_type: ptype, person_id: person.person_id, date: target,
        check_in: null, status: 'Absent',
      });
      loadAll();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const openEditTimes = (row) => {
    setEditTimes(row);
    setEditForm({
      check_in: row.check_in_input || '',
      check_out: row.check_out_input || '',
    });
  };

  const handleSaveTimes = async () => {
    if (!editTimes) return;
    setSavingTimes(true);
    try {
      await api.put(`/staff-attendance/${editTimes.id}/admin-edit-times`, {
        check_in: editForm.check_in || '',
        check_out: editForm.check_out || '',
      });
      setEditTimes(null);
      loadAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingTimes(false);
    }
  };

  // ─── Filtered rows ────────────────────────────────────
  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
      if (search && !(r.person_name || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, search, categoryFilter]);

  const fmtTime = (row, key) => row[`${key}_local`] || '—';
  const showDateColumn = dateFrom !== dateTo || !dateFrom;

  // ─── Category counts ─────────────────────────────────
  const categoryCounts = useMemo(() => ({
    teaching:       filtered.filter(r => r.category === 'Teaching Faculty').length,
    administrative: filtered.filter(r => r.category === 'Administrative Staff').length,
    support:        filtered.filter(r => r.category === 'Support Staff').length,
    total:          filtered.length,
  }), [filtered]);

  const statusCounts = useMemo(() => ({
    present: filtered.filter(r => r.status === 'Present').length,
    absent:  filtered.filter(r => r.status === 'Absent').length,
    late:    filtered.filter(r => r.status === 'Late').length,
    halfDay: filtered.filter(r => r.status === 'Half-Day').length,
    leave:   filtered.filter(r => r.status === 'Leave').length,
  }), [filtered]);

  // ─── People with no records in the range ──────────────
  const missing = useMemo(() => {
    const seen = new Set(rows.map(r => `${r.person_type}:${r.person_id}`));
    return allPeople.filter(p => {
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      return !seen.has(`${p.person_type}:${p.person_id}`);
    });
  }, [rows, allPeople, categoryFilter]);

  const shownTz = rows[0]?.timezone || 'Asia/Karachi';

  // ─── Handle category card click ───────────────────────
  const handleCategoryCardClick = (cat) => {
    setCategoryFilter(prev => prev === cat ? 'all' : cat);
  };

  const handleStatusCardClick = (status) => {
    setStatusFilter(prev => prev === status ? 'all' : status);
  };

  const isFilteringCategory = categoryFilter !== 'all';
  const isFilteringStatus = statusFilter !== 'all';

  return (
    <div>
      {/* ══════════════════════════════════════════════════
          Header
      ══════════════════════════════════════════════════ */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">🕐 Staff & Teacher Attendance</h2>
        <div className="flex gap-2">
          <button onClick={handleBulkInit} disabled={busy}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm disabled:opacity-50">
            <i className="fas fa-list-check mr-1"></i> Pre-mark All Absent
          </button>
          <button onClick={loadAll} disabled={loading}
            className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200 text-sm">
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          Timezone banner
      ══════════════════════════════════════════════════ */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 text-xs px-3 py-2 rounded mb-3 flex items-center gap-2">
        <i className="fas fa-globe"></i>
        Times shown in <strong>{shownTz}</strong>
        {isAdmin && (
          <span className="ml-auto text-blue-600">
            (Change this in <strong>Admin Settings</strong>)
          </span>
        )}
      </div>

      {/* ══════════════════════════════════════════════════
          Date presets
      ══════════════════════════════════════════════════ */}
      <div className="bg-white p-3 rounded shadow mb-3 flex flex-wrap gap-2 items-center">
        <span className="text-xs font-semibold text-gray-600 mr-1">Range:</span>
        {[
          ['today', 'Today'],
          ['yesterday', 'Yesterday'],
          ['week', 'Last 7 days'],
          ['30days', 'Last 30 days'],
          ['month', 'This Month'],
          ['all', 'All Time'],
        ].map(([key, label]) => (
          <button key={key}
            onClick={() => applyPreset(key)}
            className={`px-3 py-1 text-xs rounded border transition ${
              activePreset === key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
            }`}>
            {label}
          </button>
        ))}

        <div className="flex items-center gap-2 ml-2">
          <label className="text-xs text-gray-500">From</label>
          <input type="date" className="p-1.5 border rounded text-xs"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setActivePreset('custom'); }} />
          <label className="text-xs text-gray-500">To</label>
          <input type="date" className="p-1.5 border rounded text-xs"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); setActivePreset('custom'); }} />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          ✅ CATEGORY CARDS — now clickable filters
      ══════════════════════════════════════════════════ */}
      <div className="grid grid-cols-4 gap-3 mb-3">
        {/* All Categories card — clears category filter */}
        <button
          type="button"
          onClick={() => setCategoryFilter('all')}
          title="Show all categories"
          className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
            !isFilteringCategory
              ? 'border-gray-700 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-500'
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-users text-gray-700"></i> All Categories
          </p>
          <p className="text-2xl font-bold text-gray-800">{categoryCounts.total}</p>
          {!isFilteringCategory && (
            <p className="text-[10px] text-gray-500 mt-1">
              <i className="fas fa-check-circle"></i> Showing all
            </p>
          )}
        </button>

        {/* Category cards — click to filter */}
        {[
          { key: 'Teaching Faculty',     count: categoryCounts.teaching,       ...CATEGORIES[1] },
          { key: 'Administrative Staff', count: categoryCounts.administrative, ...CATEGORIES[2] },
          { key: 'Support Staff',        count: categoryCounts.support,        ...CATEGORIES[3] },
        ].map(({ key, count, label, icon, color, border, bg }) => {
          const isActive = categoryFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleCategoryCardClick(key)}
              title={isActive ? 'Click to clear filter' : `Filter by ${label}`}
              className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                isActive
                  ? `${border} ring-2 ring-offset-1 ring-gray-300 ${bg}`
                  : `${border.replace('500', '200')} hover:${border} hover:shadow-md`
              }`}
            >
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className={`fas ${icon} ${color}`}></i> {label}
              </p>
              <p className={`text-2xl font-bold ${color}`}>{count}</p>
              {isActive && (
                <p className={`text-[10px] mt-1 ${color} font-semibold`}>
                  <i className="fas fa-filter"></i> Filter active — click to clear
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════
          ✅ STATUS CARDS — now clickable status filters
      ══════════════════════════════════════════════════ */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        {[
          { key: 'Present',  count: statusCounts.present, label: 'Present',  color: 'text-green-700',  border: 'border-green-500',  bg: 'bg-green-50' },
          { key: 'Absent',   count: statusCounts.absent,  label: 'Absent',   color: 'text-red-700',    border: 'border-red-500',    bg: 'bg-red-50' },
          { key: 'Late',     count: statusCounts.late,    label: 'Late',     color: 'text-yellow-700', border: 'border-yellow-500', bg: 'bg-yellow-50' },
          { key: 'Half-Day', count: statusCounts.halfDay, label: 'Half-Day', color: 'text-orange-700', border: 'border-orange-500', bg: 'bg-orange-50' },
          { key: 'Leave',    count: statusCounts.leave,   label: 'Leave',    color: 'text-blue-700',   border: 'border-blue-500',   bg: 'bg-blue-50' },
        ].map(({ key, count, label, color, border, bg }) => {
          const isActive = statusFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleStatusCardClick(key)}
              title={isActive ? 'Click to clear status filter' : `Filter by ${label}`}
              className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                isActive
                  ? `${border} ring-2 ring-offset-1 ring-gray-300 ${bg}`
                  : `${border.replace('500', '200')} hover:${border} hover:shadow-md`
              }`}
            >
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{count}</p>
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════
          Active filter chips + search
      ══════════════════════════════════════════════════ */}
      {(isFilteringCategory || isFilteringStatus || search) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Active filters:</span>
          {isFilteringCategory && (
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${categoryBadge(categoryFilter)}`}>
              {categoryFilter}
              <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:opacity-70 font-bold" title="Clear">×</button>
            </span>
          )}
          {isFilteringStatus && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter('all')} className="ml-1 hover:opacity-70 font-bold" title="Clear">×</button>
            </span>
          )}
          {search && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
              Search: "{search}"
              <button onClick={() => setSearch('')} className="ml-1 hover:opacity-70 font-bold" title="Clear">×</button>
            </span>
          )}
        </div>
      )}

      {/* ─── Search bar ─────────────────────────────────── */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <input type="text" placeholder="Search by name..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search} onChange={e => setSearch(e.target.value)} />
        <span className="text-sm text-gray-500">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ─── Missing people ─────────────────────────────── */}
      {missing.length > 0 && !showDateColumn && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
          <p className="text-sm font-semibold text-yellow-800 mb-2">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            {missing.length} {missing.length === 1 ? 'person has' : 'people have'} no record for this day
          </p>
          <div className="flex flex-wrap gap-2">
            {missing.slice(0, 30).map(m => (
              <button key={`${m.person_type}-${m.person_id}`}
                onClick={() => handleManualAdd(m.person_type, m)}
                className="px-3 py-1 text-xs bg-white border border-yellow-300 hover:bg-yellow-100 rounded">
                + {m.name}
                <span className="text-gray-500 ml-1 font-medium">
                  ({m.designation || m.person_type})
                </span>
              </button>
            ))}
            {missing.length > 30 && (
              <span className="text-xs text-gray-500 self-center">+{missing.length - 30} more…</span>
            )}
          </div>
        </div>
      )}

      {/* ─── Table ─────────────────────────────────────── */}
      <div className="bg-white p-4 rounded shadow">
        {loading ? <p className="text-gray-500">Loading…</p> : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-user-clock text-4xl mb-3"></i>
            <p>No attendance records for this range.</p>
            <button onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); setSearch(''); }}
              className="mt-3 text-blue-600 hover:underline text-sm">
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    ...(showDateColumn ? ['Date'] : []),
                    'Name', 'Category', 'Check-In', 'Check-Out',
                    'Hours', 'Status', 'Source', 'Actions',
                  ].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    {showDateColumn && (
                      <td className="p-3 font-mono text-xs text-gray-600">{r.date}</td>
                    )}
                    <td className="p-3 font-medium">{r.person_name}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold inline-block w-fit ${categoryBadge(r.category)}`}>
                          {r.category}
                        </span>
                        <span className="text-[10px] text-gray-600 font-medium">
                          {r.designation || r.person_type}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{fmtTime(r, 'check_in')}</td>
                    <td className="p-3 font-mono text-xs">{fmtTime(r, 'check_out')}</td>
                    <td className="p-3">{r.worked_hours ? `${r.worked_hours}h` : '—'}</td>
                    <td className="p-3">
                      <select value={r.status} onChange={e => handleStatusChange(r, e.target.value)}
                        className={`px-2 py-1 text-xs rounded border-0 font-semibold ${
                          r.status === 'Present' ? 'bg-green-100 text-green-800'
                          : r.status === 'Absent' ? 'bg-red-100 text-red-800'
                          : r.status === 'Late' ? 'bg-yellow-100 text-yellow-800'
                          : r.status === 'Half-Day' ? 'bg-orange-100 text-orange-800'
                          : 'bg-blue-100 text-blue-800'
                        }`}>
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3"><span className="text-xs text-gray-500">{r.source}</span></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {!r.check_in && (
                          <button onClick={() => handleCheckIn(r)}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">In</button>
                        )}
                        {r.check_in && !r.check_out && (
                          <button onClick={() => handleCheckOut(r)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">Out</button>
                        )}
                        {r.check_in && r.check_out && (
                          <span className="text-xs text-gray-400 px-2 py-1">Done ✓</span>
                        )}
                        {isAdmin && (
                          <button onClick={() => openEditTimes(r)}
                            title="Admin: edit times"
                            className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200">
                            <i className="fas fa-clock"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── ADMIN: Edit Times Modal ───────────────────── */}
      {isAdmin && editTimes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">
                <i className="fas fa-clock text-yellow-600 mr-2"></i>
                Edit Times — Admin Only
              </h3>
              <button onClick={() => setEditTimes(null)}
                className="text-2xl text-gray-400 hover:text-gray-700 leading-none">×</button>
            </div>

            <div className="bg-gray-50 p-3 rounded text-sm mb-4">
              <p className="font-semibold">{editTimes.person_name}</p>
              <p className="text-xs text-gray-500">
                {editTimes.date} · {editTimes.category}
                {editTimes.designation ? ` · ${editTimes.designation}` : ''}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Timezone: <strong>{editTimes.timezone}</strong>
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Check-In</label>
                <input type="datetime-local"
                  className="w-full p-2 border rounded"
                  value={editForm.check_in}
                  onChange={e => setEditForm({ ...editForm, check_in: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Check-Out</label>
                <input type="datetime-local"
                  className="w-full p-2 border rounded"
                  value={editForm.check_out}
                  onChange={e => setEditForm({ ...editForm, check_out: e.target.value })} />
              </div>
              <p className="text-xs text-gray-500 italic">
                Clear a field to remove that time. Entered in <strong>{editTimes.timezone}</strong>.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
              <button onClick={() => setEditTimes(null)} disabled={savingTimes}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleSaveTimes} disabled={savingTimes}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {savingTimes ? 'Saving…' : '💾 Save Times'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}